import dayjs, { type Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { getBrowserTimezone } from "./timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * "Today's schedule" is the clinic's day sheet, so it is reckoned in the clinic's zone, not the
 * viewer's. The zone is the account's stored timezone (the one `App.tsx` writes from the
 * clinician's own browser on every load; GOD mode never overwrites it), so an admin in another
 * zone or a remote scribe sees the slots exactly as the EHR lists them, on the clinic's day.
 * With no usable stored zone, the browser's zone is used — the same helper the app is about to
 * store, so the fallback and the persisted value agree.
 */
export const resolveClinicZone = (clinicTz?: string | null): string => {
  const tz = (clinicTz || "").trim();
  if (tz) {
    try {
      dayjs().tz(tz); // throws RangeError on an unknown zone
      return tz;
    } catch {
      // fall through to the browser
    }
  }
  return getBrowserTimezone();
};

/** Query params for the schedule sort: the clinic's local day as UTC instants, `[from, to)`.
 *  Computed at fetch time, so a tab left open overnight asks for the new day on its next refetch. */
export const todayScheduleWindow = (
  clinicTz?: string | null,
  now: Dayjs = dayjs(),
): { schedule_from: string; schedule_to: string } => {
  const start = now.tz(resolveClinicZone(clinicTz)).startOf("day");
  return { schedule_from: start.toISOString(), schedule_to: start.add(1, "day").toISOString() };
};

/** Slot time for the row under the schedule sort, e.g. "9:30 AM", in `zone` (an already
 *  resolved IANA name — resolve once per render, not per row). */
export const scheduleSlotLabel = (scheduledAt: string | null | undefined, zone: string): string => {
  if (!scheduledAt) return "";
  const at = dayjs(scheduledAt);
  return at.isValid() ? at.tz(zone).format("h:mm A") : "";
};
